import { FormEvent } from 'react';

interface Step4Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function Step4Availability({ data, onChange, onNext, onBack }: Step4Props) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!data.minStay) return;
    onNext();
  };

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 4 of 6</p>
      <h2 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">Availability.</h2>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        Tell us how you manage bookings and what guests need to know before they arrive.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-7">
        {/* Booking method */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Booking Method</label>
          <p className="text-[#77776f] text-xs font-sans -mt-0.5">
            Describe how you currently handle bookings — any software or platforms you use (e.g. Airbnb, VRBO, your own website), and how you&apos;d like to handle enquiries going forward.
          </p>
          <textarea
            name="bookingMethod"
            value={data.bookingMethod || ''}
            onChange={(e) => onChange('bookingMethod', e.target.value)}
            maxLength={500}
            rows={5}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="e.g. We currently list on Airbnb and have our own site at villa.com. Bookings come through both channels — we&apos;d love to keep using our site but are open to how you handle new enquiries going forward..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.bookingMethod || '').length}/500</p>
        </div>

        {/* Minimum stay */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Minimum Stay (nights)</label>
          <p className="text-[#77776f] text-xs font-sans -mt-0.5">
            The shortest stay you&apos;ll accept for a booking.
          </p>
          <input
            type="number"
            name="minStay"
            required
            min={1}
            max={365}
            value={data.minStay || ''}
            onChange={(e) => onChange('minStay', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors w-40"
            placeholder="7"
          />
        </div>

        {/* Check-in process */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Check-in Process</label>
          <p className="text-[#77776f] text-xs font-sans -mt-0.5">
            Walk us through how guests check in — key collection, access codes, meet &amp; greet, anything relevant.
          </p>
          <textarea
            name="checkInProcess"
            value={data.checkInProcess || ''}
            onChange={(e) => onChange('checkInProcess', e.target.value)}
            maxLength={500}
            rows={4}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="e.g. Guests collect a key from a lockbox at the front gate using a code sent 24 hours before arrival. A welcome pack is left on the kitchen table..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.checkInProcess || '').length}/500</p>
        </div>

        {/* House rules */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            House Rules &amp; Special Requirements
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <p className="text-[#77776f] text-xs font-sans -mt-0.5">
            Anything guests must know or agree to — noise curfews, no pets, pool supervision, smoking rules, damage deposits, etc.
          </p>
          <textarea
            name="houseRules"
            value={data.houseRules || ''}
            onChange={(e) => onChange('houseRules', e.target.value)}
            maxLength={500}
            rows={4}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors resize-none"
            placeholder="e.g. No parties or events. Quiet hours between 11pm–8am. No smoking inside. Children must be supervised around the pool at all times. A €500 security deposit is held on arrival..."
          />
          <p className="text-[#77776f] text-xs font-sans text-right">{(data.houseRules || '').length}/500</p>
        </div>

        <div className="flex items-center gap-4 mt-2">
          <button type="button" onClick={onBack} className="text-sm font-sans text-[#77776f] hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="submit"
            disabled={!data.minStay}
            className="bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
